import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { listDatasets, listRows } from '@/features/datasets/datasets-api';
import {
  type ExportMode,
  type ExportRecord,
  createExport,
  downloadExport,
  listExports,
} from '@/features/exports/exports-api';
import { listMappings } from '@/features/mappings/mappings-api';
import { listTemplates } from '@/features/templates/templates-api';
import { useFormatters } from '@/i18n/format';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  CheckCircle2,
  Download,
  ExternalLink,
  Loader2,
  Play,
  TriangleAlert,
  Upload,
} from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/app/exports')({
  component: ExportsPage,
});

function ExportsPage(): ReactElement {
  const { t } = useTranslation();
  const { dateTime } = useFormatters();
  const exports_ = useQuery({ queryKey: ['exports'], queryFn: listExports });
  const list = exports_.data?.exports ?? [];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('app.layout.section.workspace', { defaultValue: 'Workspace' })}
        title={t('exports.list.title')}
        description={t('exports.list.description')}
      />

      <NewExportCard />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t('exports.list.recentHeader')}
        </h2>
        {exports_.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            icon={Upload}
            title={t('exports.list.empty')}
            description={t('exports.list.emptyBody', {
              defaultValue: 'Run an export above to see results here.',
            })}
          />
        ) : (
          <ul className="space-y-2">
            {list.map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-card p-4 shadow-soft"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className={
                      e.status === 'done'
                        ? 'inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                        : e.status === 'error'
                          ? 'inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive'
                          : 'inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary'
                    }
                  >
                    {e.status === 'done' ? (
                      <CheckCircle2 className="size-4" aria-hidden />
                    ) : e.status === 'error' ? (
                      <TriangleAlert className="size-4" aria-hidden />
                    ) : (
                      <Loader2 className="size-4 animate-spin" aria-hidden />
                    )}
                  </span>
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="px-1.5 text-[10px] uppercase">
                        {e.mode}
                      </Badge>
                      <span className="truncate font-mono text-xs text-muted-foreground">
                        {e.id}
                      </span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {dateTime(e.createdAt)}
                      {' · '}
                      {e.outputSizeBytes ? `${Math.round(e.outputSizeBytes / 1024)} KB` : '—'}
                      {e.errorMessage ? ` · ${e.errorMessage}` : null}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {e.status === 'done' && e.outputKey ? <DownloadButton record={e} /> : null}
                  {e.status === 'done' && e.outputGoogleFileId ? (
                    <a
                      href={`https://drive.google.com/file/d/${e.outputGoogleFileId}/view`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button variant="outline" size="sm">
                        <ExternalLink className="size-3.5" aria-hidden />
                        {t('exports.list.openInDrive')}
                      </Button>
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function DownloadButton({ record }: { record: ExportRecord }): ReactElement {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={pending}
      onClick={async () => {
        setPending(true);
        try {
          const ext = record.mode === 'gdoc' ? 'html' : record.mode;
          await downloadExport(record.id, `${record.id}.${ext}`);
        } catch (err) {
          toast.error(
            err instanceof ApiError
              ? err.message
              : t('exports.list.downloadFailed', { defaultValue: 'Download failed' }),
          );
        } finally {
          setPending(false);
        }
      }}
    >
      {pending ? (
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
      ) : (
        <Download className="size-3.5" aria-hidden />
      )}
      {t('exports.list.download')}
    </Button>
  );
}

type RowSelection = 'first' | 'all' | `row:${string}`;

function NewExportCard(): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState('');
  const [mappingId, setMappingId] = useState('');
  const [datasetId, setDatasetId] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelection>('first');
  const [mode, setMode] = useState<ExportMode>('pdf');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({ queryKey: ['templates'], queryFn: listTemplates });
  const mappings = useQuery({
    queryKey: ['mappings', templateId],
    queryFn: () => listMappings(templateId),
    enabled: !!templateId,
  });
  const datasets = useQuery({ queryKey: ['datasets'], queryFn: listDatasets });
  const rows = useQuery({
    queryKey: ['datasets', 'rows', datasetId],
    queryFn: () => listRows(datasetId, 1, 500),
    enabled: !!datasetId,
  });

  const isEmailMode = mode === 'email';
  const recipientLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail);

  const create = useMutation({
    mutationFn: () => {
      const datasetRowId = rowSelection.startsWith('row:') ? rowSelection.slice(4) : undefined;
      const allRows = rowSelection === 'all' ? true : undefined;
      return createExport({
        mode,
        templateId,
        mappingId,
        datasetId: datasetId || undefined,
        datasetRowId,
        allRows,
        ...(isEmailMode
          ? {
              recipientEmail: recipientEmail || undefined,
              subject: subject || undefined,
              bodyText: bodyText || undefined,
            }
          : {}),
      });
    },
    onSuccess: (data) => {
      if (data.isSingle) {
        const status = data.exports[0]?.status;
        if (status === 'done') {
          toast.success(t('exports.create.successDone'));
        } else if (status === 'error') {
          toast.error(
            t('exports.create.errorResult', {
              message: data.exports[0]?.errorMessage ?? t('exports.create.unknownError'),
            }),
          );
        } else {
          toast.info(t('exports.create.queued', { status }));
        }
      } else {
        const ok = data.exports.filter((e) => e.status === 'done').length;
        const failed = data.exports.length - ok;
        if (failed === 0) {
          toast.success(t('exports.create.batchAllDone', { count: data.total }));
        } else {
          toast.warning(t('exports.create.batchPartial', { ok, failed, total: data.total }));
        }
      }
      queryClient.invalidateQueries({ queryKey: ['exports'] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === 'batch_too_large') {
          setError(t('exports.create.batchTooLarge', { message: err.message }));
        } else if (err.code === 'email_send_not_allowed') {
          setError(t('exports.create.emailSendNotAllowed'));
        } else if (err.code === 'email_send_failed') {
          setError(t('exports.create.emailSendFailed', { message: err.message }));
        } else {
          setError(err.message);
        }
      } else {
        setError(t('exports.create.failedStart'));
      }
    },
  });

  const matchingDatasets = (datasets.data?.datasets ?? []).filter(
    (d) => !mappingId || d.mappingId === mappingId,
  );

  return (
    <Card className="border-border/70 shadow-soft">
      <CardHeader>
        <div className="flex items-start gap-3">
          <span className="inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Play className="size-4" aria-hidden />
          </span>
          <div className="space-y-1">
            <CardTitle className="text-base">{t('exports.create.title')}</CardTitle>
            <CardDescription>{t('exports.create.description')}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertTitle>{t('exports.create.errorTitle')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="ex-template">{t('exports.create.template')}</Label>
            <select
              id="ex-template"
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setMappingId('');
                setDatasetId('');
                setRowSelection('first');
              }}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">{t('exports.create.chooseOption')}</option>
              {templates.data?.templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ex-mapping">{t('exports.create.mapping')}</Label>
            <select
              id="ex-mapping"
              value={mappingId}
              onChange={(e) => {
                setMappingId(e.target.value);
                setDatasetId('');
                setRowSelection('first');
              }}
              disabled={!templateId}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
            >
              <option value="">{t('exports.create.chooseOption')}</option>
              {mappings.data?.mappings.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} (v{m.version})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ex-dataset">{t('exports.create.dataset')}</Label>
            <select
              id="ex-dataset"
              value={datasetId}
              onChange={(e) => {
                setDatasetId(e.target.value);
                setRowSelection('first');
              }}
              disabled={!mappingId}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
            >
              <option value="">{t('exports.create.chooseOption')}</option>
              {matchingDatasets.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({t('exports.create.rowsCount', { count: d.rowCount })})
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ex-row">{t('exports.create.row')}</Label>
            <select
              id="ex-row"
              value={rowSelection}
              onChange={(e) => setRowSelection(e.target.value as RowSelection)}
              disabled={!datasetId}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
            >
              <option value="first">{t('exports.create.firstRow')}</option>
              <option value="all">{t('exports.create.allRows')}</option>
              {rows.data?.rows.map((r, idx) => {
                const data = r.data as Record<string, unknown>;
                const naam = typeof data.naam === 'string' ? data.naam.trim() : '';
                return (
                  <option key={r.id} value={`row:${r.id}`}>
                    {naam
                      ? t('exports.create.rowWithName', { n: idx + 1, name: naam })
                      : t('exports.create.rowNumber', { n: idx + 1 })}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ex-mode">{t('exports.create.mode')}</Label>
            <select
              id="ex-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as ExportMode)}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="pdf">{t('exports.create.modePdf')}</option>
              <option value="docx">{t('exports.create.modeDocx')}</option>
              <option value="xlsx">{t('exports.create.modeXlsx')}</option>
              <option value="gdoc">{t('exports.create.modeGdoc')}</option>
              <option value="email">{t('exports.create.modeEmail')}</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ex-name">{t('exports.create.nameLabel')}</Label>
            <Input id="ex-name" disabled placeholder={t('exports.create.namePlaceholder')} />
          </div>
        </div>
        {isEmailMode ? (
          <div className="grid gap-4 rounded-lg border border-border/70 bg-muted/30 p-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ex-recipient">{t('exports.create.recipientLabel')}</Label>
              <Input
                id="ex-recipient"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder={t('exports.create.recipientPlaceholder')}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ex-subject">{t('exports.create.subjectLabel')}</Label>
              <Input
                id="ex-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('exports.create.subjectPlaceholder')}
              />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label htmlFor="ex-body">{t('exports.create.bodyLabel')}</Label>
              <textarea
                id="ex-body"
                rows={3}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                placeholder={t('exports.create.bodyPlaceholder')}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>
          </div>
        ) : null}
        <Button
          onClick={() => create.mutate()}
          disabled={
            !templateId || !mappingId || create.isPending || (isEmailMode && !recipientLooksValid)
          }
          className="bg-foreground text-background hover:opacity-95"
        >
          {create.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Play className="size-4" aria-hidden />
          )}
          {t('exports.create.submit')}
        </Button>
      </CardContent>
    </Card>
  );
}
